import {Children, useEffect, useLayoutEffect, useMemo, useReducer, useRef} from 'react'
import type {ReactNode, ReactElement} from 'react'

import {useMutate} from '../../../utils/fastdom'
import isVisualTest from '../../../utils/isVisualTest'

type Props = {
  children?: ReactNode
  budget?: number
  cooldown?: number
  initialCount?: number
  loadingPlaceholder?: ReactNode
}
const isTest = process.env.NODE_ENV === 'test' || isVisualTest()
const second = 1000
const fps = 60
const COOLDOWN = 500
const BUDGET = second / fps

type State = {
  count: number
  delta: number
}

export function useIncremental(
  total: number,
  initialCount = 1,
  budget = BUDGET,
  cooldown = COOLDOWN,
) {
  const renderTime = useRef(budget)
  const timeStamp = performance.now()
  useLayoutEffect(() => {
    renderTime.current = performance.now() - timeStamp
  })
  const [{count}, increment] = useReducer(
    (state: State, timeSpent: number) => {
      const delta = Math.ceil((state.delta * budget) / timeSpent)
      return {
        delta,
        count: state.count + delta,
      }
    },
    {
      count: initialCount,
      delta: 1,
    },
  )
  const mutate = useMutate()
  useEffect(() => {
    if (!isTest && count < total) {
      const timeout = setTimeout(() => mutate(() => increment(renderTime.current)), cooldown)
      return () => clearTimeout(timeout)
    }

    return undefined
  }, [cooldown, total, count, mutate, timeStamp])
  return count
}

function Incremental({
  children,
  budget = BUDGET,
  cooldown = COOLDOWN,
  initialCount = 1,
  loadingPlaceholder = null,
}: Props): ReactElement | null {
  const childrenArray = useMemo(() => Children.toArray(children), [children])
  const count = useIncremental(childrenArray.length, initialCount, budget, cooldown)

  if (isTest) {
    return <>{childrenArray}</>
  }

  const arrayToRender = childrenArray.slice(0, count)
  const shouldShowloadingPlaceholder =
    loadingPlaceholder != null && childrenArray.length !== arrayToRender.length
  return (
    <>
      {arrayToRender}
      {shouldShowloadingPlaceholder && loadingPlaceholder}
    </>
  )
}

export default Incremental
